// Estado global do console: quem está logado, organizações visíveis e o escopo selecionado
// (organização, unidade, período, comparar). Trocar de organização ou sair cancela as consultas
// em voo e, no logout, limpa o cache inteiro — dado de um escopo nunca aparece em outro.
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "@/api/client";
import type { Me, Organization, Period, Unit } from "@/api/types";
import type { Scope } from "@/api/queryKey";
import { isInternal } from "@/access/permissions";
import { logout } from "@/auth/session";

export interface ScopeState {
  org: string;
  unit: string;
  period: Period;
  from?: string;
  to?: string;
  compare: boolean;
}

interface ConsoleValue {
  me: Me;
  orgs: Organization[];
  scope: Scope;
  compare: boolean;
  /** Unidades visíveis na organização selecionada. */
  units: Unit[];
  orgName: (id: string) => string;
  unitName: (id: string) => string;
  setOrg: (org: string) => void;
  setUnit: (unit: string) => void;
  setPeriod: (period: Period, range?: { from: string; to: string }) => void;
  setCompare: (v: boolean) => void;
  replaceMe: (me: Me) => void;
  logout: () => void;
}

const Ctx = createContext<ConsoleValue | null>(null);

export function initialScope(me: Me): ScopeState {
  const org = me.org === "internal" ? "all" : me.org;
  const unit = me.units.includes("all") ? "all" : (me.units[0] ?? "all");
  return { org, unit, period: "7d", compare: true };
}

export const PERIOD_LABELS: Record<Period, string> = {
  yesterday: "Ontem",
  "7d": "7 dias",
  "30d": "30 dias",
  custom: "Personalizado",
};

export function ConsoleProvider(props: {
  me: Me;
  onMeChange: (me: Me | null) => void;
  children: ReactNode;
}) {
  const { me, onMeChange } = props;
  const qc = useQueryClient();
  const [state, setState] = useState<ScopeState>(() => initialScope(me));

  const orgsQ = useQuery({
    queryKey: ["organizations", me.id],
    queryFn: ({ signal }) => unwrap(api.GET("/organizations", { signal })).then((r) => r.items),
    staleTime: Infinity,
  });
  const orgs = useMemo(() => orgsQ.data ?? [], [orgsQ.data]);

  const allUnits = useMemo(() => orgs.flatMap((o) => o.units), [orgs]);
  const units = useMemo(() => {
    const list = state.org === "all" ? allUnits : (orgs.find((o) => o.id === state.org)?.units ?? []);
    if (isInternal(me) || me.units.includes("all")) return list;
    return list.filter((u) => me.units.includes(u.id));
  }, [allUnits, orgs, state.org, me]);

  const orgName = useCallback(
    (id: string) => (id === "all" ? "Operação Iuvo" : (orgs.find((o) => o.id === id)?.name ?? "—")),
    [orgs],
  );
  const unitName = useCallback(
    (id: string) => allUnits.find((u) => u.id === id)?.name ?? "Todas as unidades",
    [allUnits],
  );

  const value = useMemo<ConsoleValue>(
    () => ({
      me,
      orgs,
      units,
      compare: state.compare,
      scope: { userId: me.id, org: state.org, unit: state.unit, period: state.period, from: state.from, to: state.to },
      orgName,
      unitName,
      setOrg: (org) => {
        void qc.cancelQueries();
        setState((s) => ({ ...s, org, unit: "all" }));
      },
      setUnit: (unit) => {
        void qc.cancelQueries();
        setState((s) => ({ ...s, unit }));
      },
      setPeriod: (period, range) => setState((s) => ({ ...s, period, from: range?.from, to: range?.to })),
      setCompare: (compare) => setState((s) => ({ ...s, compare })),
      replaceMe: (next) => {
        void qc.cancelQueries();
        qc.clear();
        setState(initialScope(next));
        onMeChange(next);
      },
      logout: () => {
        void qc.cancelQueries();
        qc.clear();
        void logout();
        onMeChange(null);
      },
    }),
    [me, orgs, units, state, orgName, unitName, qc, onMeChange],
  );

  return <Ctx.Provider value={value}>{props.children}</Ctx.Provider>;
}

export function useConsole(): ConsoleValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useConsole fora do ConsoleProvider");
  return v;
}
